import React from 'react';
import { useInstance } from '../support/ioc/useInstance';
import { FleetManagementState } from './FleetManagementState.ts';
import { AircraftResponse } from './FleetManagmentClient.ts';
import { useSubscription } from '@autopylot-internal/state';
import { IonCard, IonText } from '@ionic/react';
import { CardHeader } from '../components/card/CardHeader.tsx';
import { CardContent } from '../components/card/CardContent.tsx';
import { DoneIcon, DroneFixedWingIcon, DroneMultiRotorIcon, DroneOtherIcon } from '../components/icons/Icons.tsx';
import { AIRCRAFT_TYPE_LABELS, AIRCRAFT_STATUS_LABELS } from './labels.ts';
import { formatLocalDateAndTime } from '../support/helpers/format.ts';
import { LabeledValue } from '../components/field/LabeledValue.tsx';
import { DefaultAircraftFooter } from './DefaultAircraftFooter.tsx';
import { AircraftStats } from './AircraftStats.tsx';
import { RevenueCatModel } from '../paywall/revenue-cat/RevenueCatModel.ts';

const getMakeAndModel = (aircraft: AircraftResponse) => {
    const space = Boolean(aircraft.make) && Boolean(aircraft.model) ? ' ': '';
    return `${aircraft.make}${space}${aircraft.model}`;
};

interface IAircraftCardProps {
    aircraft: AircraftResponse;
    onClick?: () => void;

    // used by mission details for lookup modal
    selected?: boolean;
    hideStats?: boolean;
    showStatus?: boolean;
    readonly?: boolean;
}

export const AircraftCard: React.FC<IAircraftCardProps> = ({ aircraft, onClick, selected, hideStats, showStatus, readonly }) => {
    const state = useInstance(FleetManagementState);
    useSubscription(() => state);

    const revenueCat = useInstance(RevenueCatModel);
    useSubscription(() => revenueCat);

    const Icon = selected ? DoneIcon : aircraft.frameType === 'rotary-wing' ? DroneMultiRotorIcon : aircraft.frameType === 'fixed-wing' ? DroneFixedWingIcon : DroneOtherIcon;
    const iconColor = selected ? 'success' : 'medium';
    const iconVariant = selected ? 'filled' : undefined;

    const plannedMissions =  hideStats ? [] : state.getPlannedMissions(aircraft);

    const getFirstPlannedMissionStart = () => {
        const firstMission = plannedMissions.sort((l, r) => l.getStartTime().localeCompare(r.getStartTime()))[0]!;
        return formatLocalDateAndTime({
            location: firstMission.getLocation(),
            time: firstMission.getStartTime(),
        });
    };

    const loading = !state.areMissionsFullLoaded();

    return (
        <IonCard
            data-id={aircraft.aircraftId}
            data-nickname={aircraft.nickname}
            style={{outline: selected ? '2px solid var(--ion-color-success)' : undefined}}
            button={Boolean(onClick)}
            onClick={onClick}
        >
            <CardHeader>
                <div style={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center'}}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexGrow: 2 }}>
                        <Icon size="medium" color={iconColor} variant={iconVariant} />
                        <IonText color="dark" style={{ fontSize: 20, fontWeight: 'bold' }}>{aircraft.nickname}</IonText>
                    </div>
                    <div>
                        {AIRCRAFT_TYPE_LABELS[aircraft.frameType]}
                    </div>
                </div>
            </CardHeader>

            <CardContent>

                <div style={{marginTop: -5, marginBottom: 10}}>
                    <LabeledValue tight label='Make/Model' value={getMakeAndModel(aircraft)} />
                    {showStatus &&
                        <LabeledValue tight label='Drone Status' color={aircraft.status === 'grounded' ? 'danger' : 'dark'} value={AIRCRAFT_STATUS_LABELS[aircraft.status]} />
                    }
                    {revenueCat.hasUpgraded() && !hideStats &&
                        <LabeledValue tight loading={loading} label='Next Mission' value={plannedMissions.length > 0 ? getFirstPlannedMissionStart() : ''} />
                    }
                </div>

                {!hideStats &&
                    <AircraftStats aircraft={aircraft} paywallTrigger="equipment card" />
                }

            </CardContent>

            <DefaultAircraftFooter readonly={readonly} aircraft={aircraft} onChange={state.updateAircraft} trackingFrom="equipment" />
        </IonCard>
    );
};
